import { useMemo } from "react";
import {
  Business as CompanyIcon,
  Storefront as WhiteLabelIcon,
  People as PeopleIcon,
  WarningAmber as OverLimitIcon,
} from "@mui/icons-material";
import { Box } from "@mui/material";
// KpiRow / coachTokens are still untyped .js neighbours — allowJs resolves them (checkJs is off),
// so their exports arrive as `any`. That's fine for this gate; convert them separately later.
import { KPI } from "./coach/KpiRow";
import { isOverLimit } from "../../enums/SubscriptionPlans";
import { t } from "./coach/coachTokens";
import type { CompanyBreakdownStats } from "../../api/generated";

// All-companies KPI row for the superadmin / company-group-admin overview. These are SYSTEM-level,
// platform-lens metrics — companies / white-label companies / trainees / over-limit companies. The
// per-trainee operational metrics (pending approval, expiring soon, check-ins) are a COMPANY-admin
// concern and live on each company's coach dashboard, not here.
//
// The tiles are display-only (non-interactive): a system admin has no company context and can't open
// the company-scoped clients/trainers pages, so clicking a total used to be a dead end. There is no
// onClick / navigate anywhere in this row anymore.
//
// Arbox money used to live here as a fifth tile; it moved to ArboxCollectionsRow, which sits above
// this row. It is a different lens (the operator's own finance, not platform metrics) and it grew to
// two cards, so it earns its own component rather than more cells here.
type CompanyOverviewKpiRowProps = {
  stats?: CompanyBreakdownStats | null;
};

const CompanyOverviewKpiRow = ({ stats }: CompanyOverviewKpiRowProps) => {
  const {
    totalCompanies,
    activeCompanies,
    totalTrainees,
    activeTrainees,
    companies,
  } = stats ?? {};

  const fmt = (v?: number | null): string => (v == null ? "—" : Number(v).toLocaleString());

  // Companies whose ACTIVE trainee count exceeds their subscription plan limit. The quota is
  // measured on active trainees only — disabled trainees don't consume a seat.
  const overLimitCount = useMemo(
    () => (companies ?? []).filter((c) => isOverLimit(c.subscriptionPlan ?? "", c.activeTraineeCount ?? 0)).length,
    [companies]
  );

  // White-label companies: those that ship their own branded app (have a bundleId).
  const whiteLabelCount = useMemo(
    () => (companies ?? []).filter((c) => !!c.bundleId).length,
    [companies]
  );

  const activeCompaniesBadge = activeCompanies == null ? null : `${Number(activeCompanies).toLocaleString()} פעילות`;
  const activeTraineesBadge = activeTrainees == null ? null : `${Number(activeTrainees).toLocaleString()} פעילים`;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        gap: "14px",
        marginBottom: "18px",
      }}
    >
      <KPI
        icon={<CompanyIcon sx={{ fontSize: 22 }} />}
        iconBg={t.greenLight}
        iconColor={t.green}
        badge={activeCompaniesBadge}
        badgeBg={t.greenLight}
        badgeColor={t.greenText}
        value={fmt(totalCompanies)}
        valueColor={t.text}
        label="סה״כ חברות"
        interactive={false}
      />
      <KPI
        icon={<WhiteLabelIcon sx={{ fontSize: 22 }} />}
        iconBg={t.blueLight}
        iconColor={t.blue}
        value={fmt(whiteLabelCount)}
        valueColor={t.text}
        label="חברות ממותגות"
        interactive={false}
      />
      <KPI
        icon={<PeopleIcon sx={{ fontSize: 22 }} />}
        iconBg={t.greenLight}
        iconColor={t.green}
        badge={activeTraineesBadge}
        badgeBg={t.greenLight}
        badgeColor={t.greenText}
        value={fmt(totalTrainees)}
        valueColor={t.text}
        label="סה״כ מתאמנים"
        interactive={false}
      />
      <KPI
        icon={<OverLimitIcon sx={{ fontSize: 22 }} />}
        iconBg={t.redLight}
        iconColor={t.red}
        badge={overLimitCount > 0 ? "דורש שדרוג" : null}
        badgeBg={t.redLight}
        badgeColor={t.redText}
        value={fmt(overLimitCount)}
        valueColor={overLimitCount > 0 ? t.red : t.text}
        label="חברות מעל המכסה"
        interactive={false}
      />
    </Box>
  );
};

export default CompanyOverviewKpiRow;
